import { useTheme } from '@emotion/react';
import { IconButton } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts/highstock';
import { DateTime } from 'luxon';
import React, { Suspense, useEffect, useRef, useState } from 'react';
import {
  darkTheme,
  lightTheme,
} from '../../../../components/highcharts/themes';

import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import ModelTrainingRoundedIcon from '@mui/icons-material/ModelTrainingRounded';
import WbCloudyRoundedIcon from '@mui/icons-material/WbCloudyRounded';
import { darken, lighten } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import HighchartsMore from 'highcharts/highcharts-more';
HighchartsMore(Highcharts); // Must load highcharts-more for arearange

import {
  Card,
  CardContent,
  Chip,
  Grid,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { formatNumber } from '@/utils/formatters';

const toUTC = (timestamp) =>
  DateTime.fromISO(timestamp, { zone: 'utc' }).toMillis();

function createArearangeData(
  predicted,
  comparison,
  useAdvancedModel,
  averageValue,
) {
  const merged = [];

  if (useAdvancedModel) {
    // Use predicted values normally
    for (let i = 0; i < predicted.length; i++) {
      const [xP, yP] = predicted[i];
      const [xC, yC] = comparison[i];

      if (xP !== xC) continue; // Ensure alignment

      merged.push([xP, yC, yP]); // Lower = comparison, Upper = predicted
    }
  } else {
    // Return a simple horizontal line at the averageValue
    if (!comparison.length) return []; // Ensure there is data

    const startX = comparison[0][0]; // First timestamp
    const endX = comparison[comparison.length - 1][0]; // Last timestamp

    return [
      [startX, averageValue],
      [endX, averageValue],
    ];
  }

  return merged;
}

const CustomLegend = ({
  chartRef,
  averages,
  handleToggleSeries,
  seriesVisibility,
}) => {
  if (
    !chartRef.current?.chart.series ||
    !seriesVisibility.length ||
    !averages.length
  ) {
    return null; // Prevent rendering if data is not ready
  }

  return (
    <Grid container spacing={2}>
      {chartRef.current.chart.series.map((series) => {
        const seriesAverages = averages.find(
          (avg) => avg.name === series.name,
        )?.averages;
        const isVisible = seriesVisibility.find(
          (s) => s.name === series.name,
        )?.visible;

        if (!series.options.isInternal && series.options.id) {
          return (
            <Grid item key={series.options.id || series.name} xs={6} lg={3}>
              <Card
                // variant="outlined"
                onClick={() => handleToggleSeries(series)}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  cursor: 'pointer',
                  // minHeight: '90px',
                  height: '100%',
                  cursor: 'pointer',
                  opacity: isVisible ? 1 : 0.3, // Dim if hidden
                  boxShadow: '0 20px 27px 0 rgb(0 0 0 / 5%)',
                  ':hover': {
                    boxShadow: '0 20px 27px 0 rgb(0 0 0 / 15%)',
                  },
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={() => {
                  chartRef.current.chart.series.forEach((s) => {
                    if (s !== series) {
                      s.group.element.style.transition = 'opacity 0.3s ease';
                      s.group.element.style.opacity = 0.2;
                    }
                  });
                }}
                onMouseLeave={() => {
                  chartRef.current.chart.series.forEach((s) => {
                    s.group.element.style.transition = 'opacity 0.3s ease';
                    s.group.element.style.opacity = 1;
                  });
                }}
              >
                <CardContent>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={series.color}
                      >
                        {series.name}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        }
      })}
    </Grid>
  );
};

const InitiativeChart = ({
  isLoading,
  selectedInitiative,
  updateInitiative,
}) => {
  const chartComponentRef = useRef(null);
  const [isChartReady, setIsChartReady] = useState(false);
  const [averages, setAverages] = useState([]);
  const [seriesVisibility, setSeriesVisibility] = useState([]);
  const [mode, setMode] = useState(
    selectedInitiative?.main?.energySavings?.AdvancedModelUsed
      ? 'advanced'
      : 'simple',
  ); // 'advanced' or 'simple'
  const { t } = useTranslation('energyxplorer-initiatives-tab');

  const theme = useTheme();

  const processPredictedEnergyUsage = (predictedData) => {
    if (!Array.isArray(predictedData)) return [];
    return predictedData.map((entry) => [
      toUTC(entry.timestamp),
      entry.value, // Already in kWh, no conversion needed
    ]);
  };

  const processBaselineEnergyEnergyUsage = (baselineData) => {
    if (!Array.isArray(baselineData)) return [];
    return baselineData.map((entry) => [
      toUTC(entry.timestamp),
      entry.value, // Already in kWh, no conversion needed
    ]);
  };

  const processComparisonEnergyUsage = (comparisonData) => {
    if (!Array.isArray(comparisonData)) return [];
    return comparisonData.map((entry) => [
      toUTC(entry.timestamp),
      entry.value, // Already in kWh, no conversion needed
    ]);
  };

  const processInitiativeEnergyUsage = (initiativeData) => {
    if (!Array.isArray(initiativeData)) return [];
    return initiativeData.map((entry) => [
      toUTC(entry.timestamp),
      entry.value, // Already in kWh, no conversion needed
    ]);
  };

  const energyData =
    mode === 'advanced'
      ? selectedInitiative?.main?.energySavings?.AdvancedEnergySavings
      : selectedInitiative?.main?.energySavings?.BasicEnergySavings;

  const generateSeries = () => {
    if (!selectedInitiative) return [];

    const baselineData = processBaselineEnergyEnergyUsage(
      energyData?.BaselineEnergyUsage,
    );
    const comparisonData = processComparisonEnergyUsage(
      energyData?.ComparisonEnergyUsage,
    );
    // const baselineAvg = getAverage(baselineData);
    // const comparisonAvg = getAverage(comparisonData);
    const baselineAvg =
      energyData?.EnergySavingsSummary?.AverageBaselineEnergyUsage;
    const comparisonAvg =
      energyData?.EnergySavingsSummary?.AverageComparisonEnergyUsage;

    const baselineStart = toUTC(selectedInitiative?.baselineStart);
    const baselineEnd = toUTC(selectedInitiative?.baselineEnd);
    const reportingStart = toUTC(selectedInitiative?.comparisonStart);
    const reportingEnd = toUTC(selectedInitiative?.comparisonEnd);

    const stepLineData = [
      [baselineStart, baselineAvg],
      [baselineEnd, baselineAvg],
      [reportingStart, comparisonAvg],
      [reportingEnd, comparisonAvg],
    ];

    // Add the predicted energy series conditionally
    const predictedEnergySeries = energyData?.PredictedEnergyUsage
      ? [
          // {
          //   id: 'predicted-energy-usage',
          //   name: 'Projekteret Forbrug',
          //   data: processPredictedEnergyUsage(projectedEnergyAnalysisData.PredictedEnergyUsage),
          //   type: 'line', // Optional: Set chart type
          //   // dashStyle: 'Dash', // Different style for distinction
          //   color: 'pink', // Custom color for visibility
          //   threshold: 10,
          //   marker: { enabled: true },
          //   dataGrouping: { enabled: false }, // Disable data grouping
          // },
          {
            id: 'baseline-energy-usage',
            name: t('chart.baseline_usage'),
            data: processBaselineEnergyEnergyUsage(
              energyData.BaselineEnergyUsage,
            ),
            type: 'line', // Optional: Set chart type
            // dashStyle: 'Dash', // Different style for distinction
            color: '#42a5f5', // Custom color for visibility
            dataGrouping: { enabled: false }, // Disable data grouping
            // marker: { enabled: true, symbol: 'circle' },
          },
          {
            id: 'initiative-energy-usage',
            name: t('chart.implementation_usage'),
            data: processInitiativeEnergyUsage(
              energyData?.InitiativeEnergyUsage,
            ),
            type: 'line', // Optional: Set chart type
            // dashStyle: 'Dash', // Different style for distinction
            color: '#ab47bc', // Custom color for visibility
            // marker: { enabled: true },
          },
          {
            id: 'comparison-energy-usage',
            name: t('chart.post_implementation_usage'),
            zIndex: 1, // Ensure visibility above other chart elements
            data: processComparisonEnergyUsage(
              energyData.ComparisonEnergyUsage,
            ),
            type: 'line', // Optional: Set chart type
            // dashStyle: 'Dash', // Different style for distinction
            color: '#42a5f5', // Custom color for visibility
            dataGrouping: { enabled: false }, // Disable data grouping
            // marker: { enabled: true },
          },
          {
            id: 'predicted-vs-comparison-band',
            type: mode === 'advanced' ? 'arearange' : 'line', // Optional: Set chart type
            name:
              mode === 'advanced'
                ? t('chart.projected_usage_without_initiative')
                : t('chart.average'),
            data: createArearangeData(
              processPredictedEnergyUsage(energyData?.PredictedEnergyUsage),
              processComparisonEnergyUsage(energyData?.ComparisonEnergyUsage),
              mode === 'advanced',
              baselineAvg,
            ),
            color: mode === 'advanced' ? '#73EC8B' : '#15B392', // fill color
            dashStyle: mode === 'advanced' ? undefined : 'Dash', // Only dashed when not using advanced model
            fillOpacity: mode === 'advanced' ? 0.3 : 0, // Remove fill if simple model
            lineWidth: mode === 'advanced' ? 2 : 4, // optional boundary lines
            dataGrouping: { enabled: false }, // Disable data grouping
            zIndex: mode === 'advanced' ? 0 : 5, // behind other series
            // marker: { enabled: false },
          },
          {
            // id: 'avg-step-line',
            name: t('chart.average_usage_baseline'),
            type: 'line',
            step: 'left', // step style
            color: 'red',
            data: stepLineData || [],
            zIndex: 5, // on top
            // marker: { enabled: false },
          },
        ]
      : [];

    return [...predictedEnergySeries];
  };

  const baselineEnd = toUTC(selectedInitiative?.baselineEnd);
  const reportingStart = toUTC(selectedInitiative?.comparisonStart);

  const [chartOptions, setChartOptions] = useState({
    credits: { enabled: false },
    chart: {
      height: 600,
      zooming: { type: 'x', mouseWheel: { enabled: false } },
      navigator: { enabled: false },
      scrollbar: { enabled: false },
      title: { text: 'Power Consumption Data' },
      events: {
        load: function () {
          setIsChartReady(true);
        },
        destroy: function () {
          setIsChartReady(false);
        },
      },
    },
    time: { useUTC: true },
    legend: {
      enabled: true,
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
    },
    rangeSelector: { enabled: false },
    xAxis: {
      type: 'datetime',
      plotLines: [
        {
          value: baselineEnd,
          color: '#ab47bc', // same color
          dashStyle: 'Dash', // or 'Dot' / 'ShortDash'
          width: 2,
          zIndex: 5, // on top of the plot band
          label: {
            text: selectedInitiative?.name,
            style: { color: '#ab47bc' },
          },
        },
      ],
      plotBands: [
        {
          color: 'rgba(183, 113, 229, 0.1)', // #B771E5 with some transparency
          from: baselineEnd,
          to: reportingStart,
          label: {
            text: '', // optional label
            style: { color: '#ab47bc' },
          },
        },
      ],
      gridLineWidth: 1,
    },
    yAxis: {
      title: {
        text: `${t('chart.energy_usage')} (kWh)`,
      },
      // offset: 40, // Increase offset for each axis
      gridLineWidth: 1,
      opposite: false, // Show on the right side
    },
    tooltip: {
      valueSuffix: ' kWh',
      valueDecimals: 2, // Display up to 2 decimal places
    },
    scrollbar: { enabled: false },
    loading: {
      labelStyle: {
        fontWeight: 'bold',
        fontSize: '16px',
        color: '#666',
      },
      style: {
        backgroundColor: 'transparent',
        opacity: 0,
      },
      showDuration: 100, // Smoothly show
      hideDuration: 100, // Smoothly hide
    },
    series: [],
    plotOptions: {
      arearange: {
        gapSize: 0, // or explicitly gapDetection: false in newer versions
      },
      line: {
        gapSize: 0, // or explicitly gapDetection: false in newer versions
      },
    },
  });

  useEffect(() => {
    if (!selectedInitiative) {
      const baselineEnd = toUTC(selectedInitiative?.baselineEnd);
      const reportingStart = toUTC(selectedInitiative?.comparisonStart);
      // Reset chart options when no data is available
      setAverages([]);
      setSeriesVisibility([]);
      setChartOptions((prev) => ({
        ...prev,
        xAxis: {
          ...prev.xAxis,
          plotLines: [
            {
              value: baselineEnd,
              color: '#B771E5', // same color
              dashStyle: 'Dash', // or 'Dot' / 'ShortDash'
              width: 2,
              zIndex: 5, // on top of the plot band
              label: {
                text: 'Initiative Start',
                style: { color: '#ab47bc' },
              },
            },
          ],
          plotBands: [
            {
              color: 'rgba(171,71,188, 0.1)', // #B771E5 with some transparency
              from: baselineEnd,
              to: reportingStart,
              label: {
                text: 'Initiative Period', // optional label
                style: { color: '#ab47bc' },
              },
            },
          ],
        },
        yAxis: {
          ...prev.yAxis,
        },
      }));
      return;
    }

    const series = generateSeries();

    // setAverages(calculateAverages(series));
    setSeriesVisibility(series.map((s) => ({ name: s.name, visible: true })));

    setChartOptions((prev) => ({
      ...prev,
      series: [...series],
      navigator: {
        enabled: false, // Keep disabled
      },
      xAxis: {
        ...prev.xAxis,
      },
      yAxis: {
        ...prev.yAxis,
      },
      loading: {
        labelStyle: {
          fontWeight: 'bold',
          fontSize: '16px',
          color: '#666',
        },
        style: {
          backgroundColor: 'transparent',
          opacity: 0,
        },
        showDuration: 100, // Smoothly show
        hideDuration: 100, // Smoothly hide
      },
    }));
  }, [selectedInitiative, updateInitiative, energyData, mode]);

  // Handle loading state using chart ref
  // useEffect(() => {
  //   const chart = chartComponentRef.current?.chart;
  //   if (chart) {
  //     if (isLoading) {
  //       chart.showLoading('Loading data...');
  //     } else {
  //       chart.hideLoading();
  //     }
  //   }
  // }, [isLoading]);

  useEffect(() => {
    const themeOptions = theme.palette.mode === 'dark' ? darkTheme : lightTheme;
    Highcharts.setOptions(themeOptions);

    if (
      isChartReady &&
      chartComponentRef.current &&
      chartComponentRef.current.chart
    ) {
      chartComponentRef.current.chart.update({
        chart: {
          backgroundColor: themeOptions.chart.backgroundColor,
        },
        title: {
          style: {
            color: themeOptions.title.style.color,
          },
        },
        xAxis: {
          ...chartOptions.xAxis,
          gridLineColor: themeOptions.xAxis.gridLineColor,
          lineColor: themeOptions.xAxis.lineColor,
          labels: {
            style: {
              color: themeOptions.xAxis.labels.style.color,
            },
          },
        },
        yAxis: [
          {
            gridLineColor: themeOptions.yAxis.gridLineColor,
            labels: {
              style: {
                color: themeOptions.yAxis.labels.style.color,
              },
            },
          },
          {
            gridLineColor: themeOptions.yAxis.gridLineColor,
            labels: {
              style: {
                color: themeOptions.yAxis.labels.style.color,
              },
            },
          },
        ],
        tooltip: {
          backgroundColor: themeOptions.tooltip.backgroundColor,
          borderColor: themeOptions.tooltip.borderColor,
        },
      });
    }
  }, [theme]);

  useEffect(() => {
    if (chartComponentRef.current?.chart.series) {
      const updatedSeriesVisibility = chartComponentRef.current.chart.series
        .filter((series) => !series.options.isInternal) // Exclude navigator or internal series
        .map((series) => ({
          name: series.name,
          visible: series.visible,
        }));

      setSeriesVisibility(updatedSeriesVisibility);
    }
  }, [chartComponentRef.current?.chart.series]);

  const handleToggleSeries = (series) => {
    const newVisibility = !series.visible;
    series.setVisible(newVisibility); // Update visibility in Highcharts

    // Update local visibility state
    setSeriesVisibility((prev) =>
      prev.map((s) =>
        s.name === series.name ? { ...s, visible: newVisibility } : s,
      ),
    );
  };

  // const isAdvancedModelUsed = selectedInitiative?.main?.energySavings?.AdvancedModelUsed;

  const energySavings =
    mode === 'advanced'
      ? selectedInitiative?.main?.energySavings?.AdvancedEnergySavings
          ?.EnergySavingsSummary?.EnergySavings
      : selectedInitiative?.main?.energySavings?.BasicEnergySavings
          ?.EnergySavingsSummary?.EnergySavings;

  const energySavingsPercentage =
    mode === 'advanced'
      ? selectedInitiative?.main?.energySavings?.AdvancedEnergySavings
          ?.EnergySavingsSummary?.EnergySavingsPercentage
      : selectedInitiative?.main?.energySavings?.BasicEnergySavings
          ?.EnergySavingsSummary?.EnergySavingsPercentage;

  const co2eSavings =
    mode === 'advanced'
      ? selectedInitiative?.main?.energySavings?.AdvancedEnergySavings
          ?.CO2eSavingsSummary?.CO2eSavings
      : selectedInitiative?.main?.energySavings?.BasicEnergySavings
          ?.CO2eSavingsSummary?.CO2eSavings;

  const co2eSavingsPercentage =
    mode === 'advanced'
      ? selectedInitiative?.main?.energySavings?.AdvancedEnergySavings
          ?.CO2eSavingsSummary?.CO2eSavingsPercentage
      : selectedInitiative?.main?.energySavings?.BasicEnergySavings
          ?.CO2eSavingsSummary?.CO2eSavingsPercentage;

  const getTrendColor = (percentage) => {
    if (percentage >= 0) {
      return 'success'; // Green for positive savings
    } else {
      return 'error'; // Red for negative savings
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <ListItemText
          primary={selectedInitiative?.name || 'Valgt Initiativ'}
          primaryTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
          secondary={selectedInitiative?.description || ''}
        />
        {/* <FormControlLabel
          control={
            <Switch
              checked={mode === 'advanced'}
              onChange={(e) => setMode(e.target.checked ? 'advanced' : 'simple')}
              color="success"
              disabled={!selectedInitiative?.main?.energySavings?.AdvancedModelUsed}
            />
          }
          label={mode === 'advanced' ? 'ML model anvendt' : 'Vægtet gennemsnit anvendt'}
          labelPlacement="start"
        /> */}
        <IconButton
          onClick={() => setMode(mode === 'advanced' ? 'simple' : 'advanced')}
          color="primary"
          disabled={!selectedInitiative?.main?.energySavings?.AdvancedModelUsed}
          sx={{
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <ModelTrainingRoundedIcon
            fontSize="large"
            sx={(theme) => ({
              color:
                mode === 'advanced'
                  ? '#4CAF50'
                  : theme.palette.mode === 'light'
                    ? 'lightgray'
                    : 'gray',
            })}
          />
        </IconButton>
      </Box>
      <Grid container sx={{ width: '100%', gap: 4, mb: 2 }}>
        <Grid item xs>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    fontWeight="bold"
                  >
                    {t('chart.co2e_reduction')}
                  </Typography>
                  <Tooltip
                    title={t('chart.co2e_reduction_description')}
                    placement="right"
                  >
                    <InfoRoundedIcon
                      fontSize="small"
                      sx={{
                        color: (theme) =>
                          theme.palette.mode === 'light' ? 'lightgray' : 'gray',
                        ml: 1,
                      }}
                    />
                  </Tooltip>
                </Box>
                <ListItem sx={{ display: 'flex', alignItems: 'center' }}>
                  <ListItemIcon>
                    <WbCloudyRoundedIcon
                      fontSize="large"
                      sx={{ color: 'gray' }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                        }}
                      >
                        {/* {co2eSavings > 0 ? <ArrowDropDownRoundedIcon fontSize="large" color="success" sx={{ mb: 1 }} /> : <ArrowDropUpRoundedIcon fontSize="large" color="error" sx={{ mb: 1 }} />} */}
                        <Typography
                          variant="h5"
                          color="textPrimary"
                          fontWeight="bold"
                          gutterBottom
                        >
                          {co2eSavings
                            ? `${formatNumber(
                                (co2eSavings / 1000).toFixed(0),
                              )} kg CO2e`
                            : '0 kg CO2e'}
                        </Typography>
                        <Chip
                          sx={{
                            marginLeft: 1,
                            fontWeight: 'bold',
                            backgroundColor: (theme) =>
                              theme.palette.mode === 'light'
                                ? lighten(
                                    theme.palette[
                                      getTrendColor(co2eSavingsPercentage)
                                    ].main,
                                    0.9,
                                  )
                                : darken(
                                    theme.palette[
                                      getTrendColor(co2eSavingsPercentage)
                                    ].main,
                                    0.8,
                                  ),
                            color: (theme) =>
                              theme.palette[
                                getTrendColor(co2eSavingsPercentage)
                              ].dark,
                            border: (theme) =>
                              `1px solid ${
                                theme.palette[
                                  getTrendColor(co2eSavingsPercentage)
                                ].light
                              }`,
                          }}
                          label={
                            co2eSavingsPercentage
                              ? `${co2eSavingsPercentage.toFixed(1)}%`
                              : '0%'
                          }
                          size={'small'}
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="textSecondary">
                        {t('chart.co2e_reduction_for')}{' '}
                        {selectedInitiative?.name || 'valgt initiativ'}
                      </Typography>
                    }
                  />
                </ListItem>
              </>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    fontWeight="bold"
                  >
                    {t('chart.energy_savings')}
                  </Typography>
                  <Tooltip
                    title={t('chart.energy_savings_description')}
                    placement="right"
                  >
                    <InfoRoundedIcon
                      fontSize="small"
                      sx={{
                        color: (theme) =>
                          theme.palette.mode === 'light' ? 'lightgray' : 'gray',
                        ml: 1,
                      }}
                    />
                  </Tooltip>
                </Box>
                <ListItem sx={{ display: 'flex', alignItems: 'center' }}>
                  <ListItemIcon>
                    <BoltRoundedIcon fontSize="large" sx={{ color: 'gray' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {/* {energySavings > 0 ? <ArrowDropDownRoundedIcon fontSize="large" color="success" sx={{ mb: 2 }} /> : <ArrowDropUpRoundedIcon fontSize="large" color="error" sx={{ mb: 2 }} />} */}
                        <Typography
                          variant="h5"
                          color="textPrimary"
                          fontWeight="bold"
                          gutterBottom
                        >
                          {energySavings
                            ? `${formatNumber(energySavings)} kWh`
                            : '0 kWh'}
                        </Typography>
                        <Chip
                          sx={{
                            marginLeft: 1,
                            fontWeight: 'bold',
                            backgroundColor: (theme) =>
                              theme.palette.mode === 'light'
                                ? lighten(
                                    theme.palette[
                                      getTrendColor(energySavingsPercentage)
                                    ].main,
                                    0.9,
                                  )
                                : darken(
                                    theme.palette[
                                      getTrendColor(energySavingsPercentage)
                                    ].main,
                                    0.8,
                                  ),
                            color: (theme) =>
                              theme.palette[
                                getTrendColor(energySavingsPercentage)
                              ].dark,
                            border: (theme) =>
                              `1px solid ${
                                theme.palette[
                                  getTrendColor(energySavingsPercentage)
                                ].light
                              }`,
                          }}
                          label={
                            energySavingsPercentage
                              ? `${energySavingsPercentage.toFixed(1)}%`
                              : '0%'
                          }
                          size={'small'}
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="textSecondary">
                        {t('chart.energy_savings_for')}{' '}
                        {selectedInitiative?.name || 'valgt initiativ'}
                      </Typography>
                    }
                  />
                </ListItem>
              </>
            </CardContent>
          </Card>
        </Grid>

        {/* {mode === 'advanced' && (
          <Grid item xs>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body1" color="textSecondary" fontWeight="bold">
                      Energibesparelse
                    </Typography>
                    <Tooltip title="Her vises den samlede CO₂e-udledning for det totale energiforbrug i den valgte periode. Brug den til at vurdere din klimaimpact og følge udviklingen over tid." placement="right">
                      <InfoRoundedIcon fontSize="small" sx={{ color: (theme) => theme.palette.mode === 'light' ? 'lightgray' : 'gray', ml: 1 }} />
                    </Tooltip>
                  </Box>
                  <ListItem sx={{ display: 'flex', alignItems: 'center' }}>
                    <ListItemIcon>
                      <BoltRoundedIcon fontSize="large" sx={{ color: 'gray' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="h6" color="textPrimary" fontWeight="bold" gutterBottom>
                            {energySavings ? `${formatNumber(energySavings.toFixed(0))} kg CO2e` : '0 kg CO2e'}
                          </Typography>
                          <Chip
                            sx={{
                              marginLeft: 1,
                              fontWeight: 'bold',
                              backgroundColor: (theme) => theme.palette.mode === 'light' ? lighten(theme.palette[getTrendColor(energySavingsPercentage)].main, 0.9) : darken(theme.palette[getTrendColor(energySavingsPercentage)].main, 0.8),
                              color: (theme) => theme.palette[getTrendColor(energySavingsPercentage)].dark,
                              border: (theme) => `1px solid ${theme.palette[getTrendColor(energySavingsPercentage)].light}`,
                            }}
                            label={energySavingsPercentage ? `${energySavingsPercentage.toFixed(1)}%` : '0%'}
                            size={'small'}
                          />
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" color="textSecondary">
                          Energibesparelse for {selectedInitiative?.name || 'valgt initiativ'}
                        </Typography>
                      }
                    />
                  </ListItem>
                </>
              </CardContent>
            </Card>
          </Grid>
        )} */}
      </Grid>

      {/* <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={mode === 'advanced'}
              onChange={(e) => setMode(e.target.checked ? 'advanced' : 'simple')}
              color="success"
              disabled={!selectedInitiative?.main?.energySavings?.AdvancedModelUsed}
            />
          }
          label={mode === 'advanced' ? 'ML model anvendt' : 'Vægtet gennemsnit anvendt'}
          labelPlacement="start"
        />
      </Box> */}
      <Suspense
        fallback={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '450px',
            }}
          >
            <CircularProgress />
          </Box>
        }
      >
        <HighchartsReact
          highcharts={Highcharts}
          constructorType={'stockChart'}
          options={chartOptions}
          ref={chartComponentRef}
        />
      </Suspense>
      <CustomLegend
        chartRef={chartComponentRef}
        averages={averages}
        handleToggleSeries={handleToggleSeries}
        seriesVisibility={seriesVisibility}
      />
    </>
  );
};

export default InitiativeChart;
